// The header's network line (issue #37): what is connected, what relays under construction will connect and when,
// and what the next Draw adds to the score. The numbers come from the simulation (PlayerView.me); this only picks
// what to say, so it is tested without a browser.
import type { PlayerView } from '@aurane/sim';

type Me = Pick<PlayerView['me'], 'connectedCount' | 'buildingCount' | 'nextReadyAt' | 'buildingReadyAt' | 'projectedScoreGain'>;

export interface NetStatus {
  connected: number;
  /** Systems a relay under construction will connect; 0 when nothing is building. */
  building: number;
  /** Real seconds until they are all connected (or the first, see `firstOnly`); null when nothing is building. */
  readyIn: number | null;
  /** The relays finish more than a minute apart: the countdown is the first one's. */
  firstOnly: boolean;
  /** Score change at the next Draw, signed text (« +3 », « −0.5 »), or null when it does not move. */
  gain: string | null;
}

/** `now` is the extrapolated simulated time, `timeScale` simulated seconds per real second. */
export function netStatus(me: Me, now: number, timeScale = 1): NetStatus {
  const building = me.buildingCount ?? 0;
  let readyIn: number | null = null;
  let firstOnly = false;
  if (building > 0 && me.nextReadyAt !== null && me.buildingReadyAt !== null) {
    firstOnly = me.buildingReadyAt - me.nextReadyAt > 60;
    readyIn = Math.max(0, ((firstOnly ? me.nextReadyAt : me.buildingReadyAt) - now) / Math.max(1e-9, timeScale));
  }
  const g = Math.round((me.projectedScoreGain ?? 0) * 10) / 10;
  const gain = g === 0 ? null : `${g > 0 ? '+' : '−'}${Number.isInteger(Math.abs(g)) ? Math.abs(g) : Math.abs(g).toFixed(1)}`;
  return { connected: me.connectedCount, building, readyIn, firstOnly, gain };
}

/** « 4 min », « < 1 min »: a countdown rounded up, so « ready in 0 min » never shows. */
export function minutesText(seconds: number): string {
  if (seconds < 60) return '< 1 min';
  const m = Math.ceil(seconds / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}` : ''}`;
}
